import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, Clock, Plus, Trash2, Save, Download } from 'lucide-react';
import { toast } from 'sonner';

const DIAS = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

export default function FeriadosHorarioPage() {
  const [tab, setTab] = useState('feriados');
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [feriados, setFeriados] = useState([]);
  const [horario, setHorario] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [ferRes, horRes] = await Promise.all([
          api.get(`/config/feriados?anio=${anio}`),
          api.get('/config/horario'),
        ]);
        setFeriados(ferRes.data.feriados || []);
        setHorario(horRes.data || {});
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, [anio]);

  const addFeriado = () => {
    setFeriados(prev => [...prev, { fecha: `${anio}-01-01`, nombre: '' }]);
  };

  const removeFeriado = (idx) => {
    setFeriados(prev => prev.filter((_, i) => i !== idx));
  };

  const updateFeriado = (idx, field, value) => {
    setFeriados(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const saveFeriados = async () => {
    setSaving(true);
    try {
      await api.put('/config/feriados', { anio, feriados });
      toast.success(`Feriados ${anio} guardados: ${feriados.length} días`);
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const [importing, setImporting] = useState(false);
  const importFromBoostr = async () => {
    setImporting(true);
    try {
      const res = await api.post(`/config/feriados/${anio}/import-boostr`);
      setFeriados(res.data?.feriados || []);
      toast.success(
        `Feriados ${anio} importados desde Boostr: ${res.data?.imported_count || 0} oficiales · ${res.data?.manual_preserved || 0} manuales preservados`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo importar desde Boostr');
    } finally { setImporting(false); }
  };

  const updateHorarioDia = (dia, field, value) => {
    setHorario(prev => ({ ...prev, [dia]: { ...prev[dia], [field]: value } }));
  };

  const saveHorario = async () => {
    setSaving(true);
    try {
      await api.put('/config/horario', horario);
      toast.success('Horario actualizado');
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="feriados-horario-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calendar className="h-6 w-6" /> Feriados y Horario
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Configuración de días feriados y horario laboral</p>
      </div>

      <div className="flex gap-2">
        <Button variant={tab === 'feriados' ? 'default' : 'outline'} onClick={() => setTab('feriados')} data-testid="tab-feriados">
          <Calendar className="h-4 w-4 mr-1" /> Feriados
        </Button>
        <Button variant={tab === 'horario' ? 'default' : 'outline'} onClick={() => setTab('horario')} data-testid="tab-horario">
          <Clock className="h-4 w-4 mr-1" /> Horario Normal
        </Button>
      </div>

      {tab === 'feriados' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Año:</label>
            <select className="h-9 rounded-md border bg-background px-3 text-sm" value={anio} onChange={e => setAnio(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={addFeriado} data-testid="add-feriado">
              <Plus className="h-4 w-4 mr-1" /> Agregar Feriado
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={importFromBoostr}
              disabled={importing}
              data-testid="btn-import-boostr"
              title="Trae los feriados oficiales chilenos desde api.boostr.cl. Los manuales que hayas agregado se preservan."
            >
              <Download className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : `Importar feriados ${anio} de Boostr`}
            </Button>
          </div>

          <div className="bg-card border rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="feriados-table">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 font-medium w-48">Fecha</th>
                <th className="text-left px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 w-12"></th>
              </tr></thead>
              <tbody>
                {feriados.sort((a, b) => a.fecha.localeCompare(b.fecha)).map((f, idx) => (
                  <tr key={idx} className="border-b" data-testid={`feriado-row-${idx}`}>
                    <td className="px-4 py-2">
                      <Input type="date" value={f.fecha} onChange={e => updateFeriado(idx, 'fecha', e.target.value)} className="w-44 h-8" />
                    </td>
                    <td className="px-4 py-2">
                      <Input value={f.nombre} onChange={e => updateFeriado(idx, 'nombre', e.target.value)} placeholder="Nombre del feriado" className="h-8" />
                    </td>
                    <td className="px-4 py-2">
                      <Button variant="ghost" size="sm" onClick={() => removeFeriado(idx)} className="h-7 w-7 p-0 text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {feriados.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">Sin feriados definidos para {anio}</div>
            )}
          </div>

          <Button onClick={saveFeriados} disabled={saving} data-testid="save-feriados">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : `Guardar Feriados ${anio}`}
          </Button>
        </div>
      )}

      {tab === 'horario' && (
        <div className="space-y-4">
          <div className="bg-card border rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="horario-table">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 font-medium w-32">Día</th>
                <th className="text-center px-4 py-3 font-medium">Entrada</th>
                <th className="text-center px-4 py-3 font-medium">Salida</th>
                <th className="text-center px-4 py-3 font-medium">Almuerzo (min)</th>
              </tr></thead>
              <tbody>
                {DIAS.map(dia => {
                  const h = horario[dia.key] || {};
                  const isWeekend = dia.key === 'sabado' || dia.key === 'domingo';
                  return (
                    <tr key={dia.key} className={`border-b ${isWeekend ? 'bg-muted/30' : ''}`} data-testid={`horario-${dia.key}`}>
                      <td className="px-4 py-2 font-medium">{dia.label}</td>
                      <td className="px-4 py-2 text-center">
                        <Input type="time" value={h.entrada || ''} onChange={e => updateHorarioDia(dia.key, 'entrada', e.target.value)} className="w-28 h-8 mx-auto text-center" />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Input type="time" value={h.salida || ''} onChange={e => updateHorarioDia(dia.key, 'salida', e.target.value)} className="w-28 h-8 mx-auto text-center" />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Input type="number" value={h.almuerzo || 0} onChange={e => updateHorarioDia(dia.key, 'almuerzo', Number(e.target.value))} className="w-20 h-8 mx-auto text-center" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Button onClick={saveHorario} disabled={saving} data-testid="save-horario">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar Horario'}
          </Button>
        </div>
      )}
    </div>
  );
}
